import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, fullscreenOnMobile = false, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Layout: flex column so a sticky header + scrollable body work cleanly.
        // Height: capped at ~90vh on desktop. On mobile (when fullscreenOnMobile)
        // we expand to nearly fullscreen so dense content (e.g. day-detail) is
        // comfortably scrollable without zooming.
        "fixed z-50 flex flex-col gap-4 border bg-background shadow-lg duration-200 overflow-y-auto overscroll-contain",
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        fullscreenOnMobile
          // Mobile: anchored to bottom edge as a sheet (full width, rounded top).
          // Desktop (sm+): explicitly reset right to auto and recenter via left:50% + translate.
          // Using explicit left-0/right-0 + sm:left-1/2 sm:right-auto avoids Tailwind's
          // `inset-x-auto` vs `left-1/2` source-order conflict that left the modal stuck
          // to the left of the viewport.
          ? "left-0 right-0 bottom-0 top-auto w-full max-w-none max-h-[92dvh] rounded-t-2xl p-5 sm:left-1/2 sm:right-auto sm:top-1/2 sm:bottom-auto sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[calc(100%-2rem)] max-w-lg sm:max-h-[90vh] sm:rounded-lg sm:p-6"
          : "left-1/2 top-1/2 w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 max-h-[90vh] rounded-lg p-6",
        className
      )}
      {...props}>
      {children}
      <DialogPrimitive.Close
        // z-20 sits above the sticky DialogHeader (z-10) so the X is never
        // covered by the title bar when the dialog scrolls. pr-8 is provided
        // on DialogHeader to give the title room next to this button.
        className="absolute right-4 top-4 z-20 rounded-sm bg-white/90 p-0.5 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      // Sticky header pinned at the top of the scroll container so the title
      // and close button remain visible while the body scrolls. pr-8 reserves
      // room for the absolute-positioned X close button (top-right, z-20).
      "shrink-0 flex flex-col space-y-1.5 text-left -mx-5 sm:-mx-6 px-5 sm:px-6 pt-1 pb-3 pr-8 sm:pr-10 border-b border-slate-100 bg-white/95 backdrop-blur sticky top-0 z-10",
      className
    )}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      // Sticky footer pinned to the bottom so action buttons (Save/Cancel) are
      // always reachable even when the body content is long.
      "shrink-0 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:space-x-2 -mx-5 sm:-mx-6 px-5 sm:px-6 pt-3 pb-1 border-t border-slate-100 bg-white sticky bottom-0",
      className
    )}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
